import { Link } from "@tanstack/react-router";

import type { Grow, PendingAction, Urgency } from "@/lib/api/types";
import { ActionRow } from "./action-row";

const URGENCY_RANK: Record<Urgency, number> = {
  HIGH: 0,
  MED: 1,
  ROUTINE: 2,
  PARKED: 3,
};

// Within a group, rows that carry a `sequence` (a multi-step AI plan) sort
// first by ascending sequence so they read in order; rows without a sequence
// fall back to the urgency → created_at sort and sit after the sequenced ones.
function sortRows(rows: PendingAction[]): PendingAction[] {
  return rows.slice().sort((a, b) => {
    const aSeq = a.sequence;
    const bSeq = b.sequence;
    if (aSeq != null && bSeq != null) {
      if (aSeq !== bSeq) return aSeq - bSeq;
    } else if (aSeq != null) {
      return -1;
    } else if (bSeq != null) {
      return 1;
    }
    const u = URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency];
    if (u !== 0) return u;
    return a.created_at.localeCompare(b.created_at);
  });
}

// ActionGroups renders the all-grows / list view grouped by source refill:
// actions that share a derived_from_refill_id sit under one small header so
// the grower sees they belong to one sequence. Actions with no source refill
// fall under "Other actions". Within each group rows sort by urgency then
// created_at. Kept visually light — a muted header, no heavy chrome.
export function ActionGroups({
  rows,
  growsById,
}: {
  rows: PendingAction[];
  growsById: Map<string, Grow>;
}) {
  // Preserve a stable group order: groups keyed by source refill first (in
  // first-seen order), "Other actions" last.
  const byRefill = new Map<string, PendingAction[]>();
  const other: PendingAction[] = [];
  for (const r of rows) {
    if (r.derived_from_refill_id) {
      const list = byRefill.get(r.derived_from_refill_id) ?? [];
      list.push(r);
      byRefill.set(r.derived_from_refill_id, list);
    } else {
      other.push(r);
    }
  }

  return (
    <div className="space-y-4">
      {[...byRefill.entries()].map(([refillID, groupRows]) => (
        <section key={refillID} className="space-y-1.5">
          <Link
            to="/refills/$sessionId"
            params={{ sessionId: refillID }}
            className="inline-block text-xs font-medium text-muted-foreground hover:underline"
          >
            From refill <code className="text-xs">{refillID.slice(-6)}</code> →
          </Link>
          <ul className="divide-y rounded-md border">
            {sortRows(groupRows).map((row) => (
              <ActionRow
                key={row.id}
                row={row}
                grow={growsById.get(row.grow_id)}
              />
            ))}
          </ul>
        </section>
      ))}

      {other.length > 0 && (
        <section className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Other actions
          </p>
          <ul className="divide-y rounded-md border">
            {sortRows(other).map((row) => (
              <ActionRow
                key={row.id}
                row={row}
                grow={growsById.get(row.grow_id)}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
